import type { ReactElement } from 'react';
import '../common/DesignSystem.css';

export interface DemandSegment {
  readonly key: 'Y' | 'J' | 'F';
  readonly label: string;
  readonly value: number;
  readonly color: string;
}

export interface DemandPopoverRow {
  readonly label: string;
  readonly value: number;
  readonly color: string;
}

export interface DemandStackedBarProps {
  readonly segments: readonly DemandSegment[];
  readonly popoverTitle: string;
  /** Shown in the hover card, e.g. offered capacity per route. */
  readonly popoverRows: readonly DemandPopoverRow[];
  readonly unit: string;
}

export const CABIN_SEGMENT_COLORS = { Y: '#2563eb', J: '#7c3aed', F: '#f59e0b' } as const;

const INT = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** Economy / Business / First demand as one stacked bar; hover or focus opens a breakdown card. */
export function DemandStackedBar({ segments, popoverTitle, popoverRows, unit }: DemandStackedBarProps): ReactElement {
  const visible = segments.filter((s) => s.value > 0);
  const total = visible.reduce((sum, s) => sum + s.value, 0);
  const summary = visible.map((s) => `${s.label} ${INT.format(s.value)}`).join(', ');
  return (
    <div className="fl-demand" tabIndex={0} role="group" aria-label={`Demand ${unit}: ${summary}`}>
      <div className="fl-demand-bar">
        {visible.map((s) => (
          <span key={s.key} className="fl-demand-seg" style={{ width: `${(s.value / total) * 100}%`, background: s.color }} />
        ))}
      </div>
      <div className="fl-demand-legend">
        {visible.map((s) => (
          <span key={s.key}>
            <span className="fl-dot" style={{ background: s.color }} />
            {s.label} ({s.key}) {INT.format(s.value)}
          </span>
        ))}
      </div>
      {popoverRows.length > 0 && (
        <div className="fl-popover" role="tooltip">
          <p className="fl-popover-title">{popoverTitle}</p>
          {popoverRows.map((row) => (
            <div className="fl-popover-row" key={row.label}>
              <span>
                <span className="fl-dot" style={{ background: row.color }} />
                {row.label}
              </span>
              <span className="fl-num">{INT.format(row.value)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
